
import React, { useState } from 'react';
import { CompanySettingModel, MemberModel, CompanyName } from '../../../models';
import { GraduationCap, Menu, X } from 'lucide-react';

export type LandingView = 'home' | 'about' | 'study-system' | 'teachers-intro' | 'academy-life' | 'notice' | 'success-stories' | 'reviews';

interface LandingHeaderProps {
    companyInfo: CompanySettingModel | null;
    activeView: LandingView;
    setActiveView: (view: LandingView) => void;
    memberProfile?: MemberModel | null;
    onStart: () => void;
    onLogout?: () => void;
    onNavigate?: (view: string) => void;
    onShowInquiry: () => void;
}

const LandingHeader: React.FC<LandingHeaderProps> = ({
    companyInfo,
    activeView,
    setActiveView,
    memberProfile,
    onStart,
    onLogout,
    onNavigate,
    onShowInquiry
}) => {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    return (
        <>
            <header className="h-16 md:h-20 flex items-center justify-between px-4 md:px-8 bg-[#0B132B]/80 backdrop-blur-xl border-b border-indigo-950/40 sticky top-0 z-50 relative">
                <div
                    className="flex items-center gap-3 shrink-0 cursor-pointer"
                    onClick={() => setActiveView('home')}
                >
                    <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center overflow-hidden shrink-0">
                        <img src={companyInfo?.logoUrl || '/logo.png'} alt="Logo" className="w-full h-full object-contain p-0.5" />
                    </div>
                    <span className="text-xl font-black text-white tracking-tight whitespace-nowrap">
                        {companyInfo?.companyName || CompanyName}
                    </span>
                </div>

                <nav className="hidden lg:flex items-center gap-8 lg:absolute lg:left-1/2 lg:-translate-x-1/2 lg:top-1/2 lg:-translate-y-1/2">
                    {[
                        { id: 'about', label: '학원소개' },
                        { id: 'teachers-intro', label: '강사소개' },
                        { id: 'study-system', label: '일대일 밀착관리' },
                        { id: 'academy-life', label: '학원생활' },
                        { id: 'notice', label: '공지사항' },
                        { id: 'success-stories', label: '명예의 전당' },
                        { id: 'reviews', label: '수강후기' }
                    ].map((item) => (
                        <button
                            key={item.id}
                            onClick={() => setActiveView(item.id as LandingView)}
                            className={`text-base font-bold transition-colors whitespace-nowrap ${activeView === item.id ? 'text-indigo-400' : 'text-slate-400 hover:text-indigo-400'}`}
                        >
                            {item.label}
                        </button>
                    ))}
                </nav>

                <div className="hidden lg:flex items-center gap-4">
                    <button
                        onClick={onShowInquiry}
                        className="hidden sm:flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-black text-indigo-300 bg-indigo-500/10 hover:bg-indigo-500/20 border border-indigo-500/20 transition-all active:scale-95 whitespace-nowrap"
                    >
                        상담신청
                    </button>

                    {memberProfile && (
                        <div className="flex items-center gap-3">
                            {/* Role Based Buttons */}
                            {memberProfile.role === 'super_admin' && (
                                <button
                                    onClick={() => onNavigate?.('super_admin')}
                                    className="bg-slate-800 text-white px-5 py-3 rounded-xl text-sm font-black shadow-lg hover:bg-slate-900 transition-all flex items-center gap-2"
                                >
                                    관리자
                                </button>
                            )}
                            {(memberProfile.role === 'manager' || memberProfile.role === 'staff') && (
                                <button
                                    onClick={() => onNavigate?.('manager')}
                                    className="bg-indigo-600 text-white px-5 py-3 rounded-xl text-sm font-black shadow-lg hover:bg-indigo-700 transition-all flex items-center gap-2"
                                >
                                    매니저
                                </button>
                            )}
                            {memberProfile.role === 'student' && (
                                <button
                                    onClick={() => onNavigate?.('student')}
                                    className="bg-emerald-500 text-white px-5 py-3 rounded-xl text-sm font-black shadow-lg hover:bg-emerald-600 transition-all flex items-center gap-2"
                                >
                                    마이홈
                                </button>
                            )}

                            <button
                                onClick={onLogout}
                                className="text-slate-400 hover:text-slate-200 font-bold text-sm px-2"
                            >
                                로그아웃
                            </button>
                        </div>
                    )}
                </div>

                <div className="flex lg:hidden items-center gap-3">
                    <button
                        onClick={() => setMobileMenuOpen(true)}
                        className="p-2 -mr-2 text-slate-300 hover:bg-slate-800/50 rounded-lg transition-colors"
                    >
                        <Menu size={24} />
                    </button>
                </div>
            </header>

            {/* Mobile Menu Overlay */}
            {
                mobileMenuOpen && (
                    <div className="fixed inset-0 z-[100] lg:hidden">
                        <div className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm animate-in fade-in" onClick={() => setMobileMenuOpen(false)} />
                        <div className="absolute right-0 top-0 bottom-0 w-[260px] bg-[#0B132B] border-l border-indigo-950/40 shadow-2xl animate-in slide-in-from-right duration-300 flex flex-col">
                            <div className="h-16 flex items-center justify-end px-4 border-b border-indigo-950/20">
                                <button
                                    onClick={() => setMobileMenuOpen(false)}
                                    className="p-2 -mr-2 text-slate-400 hover:text-slate-200 hover:bg-slate-800/50 rounded-lg transition-colors"
                                >
                                    <X size={24} />
                                </button>
                            </div>
                            <div className="flex-1 overflow-y-auto py-5 px-5 space-y-5">
                                <nav className="flex flex-col space-y-1">
                                    {[
                                        { id: 'about', label: '학원소개' },
                                        { id: 'teachers-intro', label: '강사소개' },
                                        { id: 'study-system', label: '일대일 밀착관리' },
                                        { id: 'academy-life', label: '학원생활' },
                                        { id: 'notice', label: '공지사항' },
                                        { id: 'success-stories', label: '명예의 전당' },
                                        { id: 'reviews', label: '수강후기' }
                                    ].map((item) => (
                                        <button
                                            key={item.id}
                                            onClick={() => { setActiveView(item.id as LandingView); setMobileMenuOpen(false); }}
                                            className={`text-left text-sm sm:text-base font-bold py-2.5 px-4 rounded-xl transition-colors ${activeView === item.id ? 'bg-indigo-500/10 text-indigo-300' : 'text-slate-300 hover:bg-slate-800/40'}`}
                                        >
                                            {item.label}
                                        </button>
                                    ))}
                                </nav>

                                <div className="pt-4 border-t border-indigo-950/20 space-y-2.5">
                                    <button
                                        onClick={() => { onShowInquiry(); setMobileMenuOpen(false); }}
                                        className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-sm font-black text-indigo-300 bg-indigo-500/10 hover:bg-indigo-500/20 border border-indigo-500/20 transition-all"
                                    >
                                        상담신청
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }
        </>
    );
};

export default LandingHeader;
